import { Card, EmptyState } from '@beautyburst/ui';
import { semanticColor, space, typeStyle } from '@beautyburst/tokens';
import { customerHref } from '../base-path.ts';
import type { CustomerApiClient } from '../api-client.ts';

type Orders = Awaited<ReturnType<CustomerApiClient['orders']['list']>>;
type OrderDetail = Awaited<ReturnType<CustomerApiClient['orders']['detail']>>;
type OrderTracking = Awaited<ReturnType<CustomerApiClient['orders']['tracking']>>;

function seller(ownershipType: string, sellerDisplayName: string): string {
  return ownershipType === 'BEAUTY_BURST_DIRECT' ? 'Beauty Burst' : sellerDisplayName;
}

function money(amountMinor: number, currency: string): string {
  return `${(amountMinor / 100).toFixed(2)} ${currency}`;
}

function fulfillmentMethod(mode: string): string {
  switch (mode) {
    case 'PICKUP':
      return 'Pickup';
    case 'DELIVERY':
      return 'Delivery';
    case 'OVERNIGHT':
      return 'Overnight delivery';
    default:
      return mode;
  }
}

function statusLabel(status: string): string {
  return status
    .toLowerCase()
    .split('_')
    .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
    .join(' ');
}

function deliveryDate(value: Date): Date {
  return value instanceof Date ? value : new Date(String(value));
}

function needsProblemSupport(order: OrderDetail): boolean {
  const attentionStatuses = new Set(['DELIVERED', 'FAILED', 'CANCELLED', 'UNDELIVERABLE']);
  return (
    attentionStatuses.has(order.status) ||
    order.subOrders.some((subOrder) => attentionStatuses.has(subOrder.status))
  );
}

export function OrdersScreen({ orders }: { orders: Orders }) {
  return (
    <section aria-labelledby="orders-title">
      <h1 id="orders-title" style={{ ...typeStyle('headingLg'), color: semanticColor.textPrimary }}>
        Orders
      </h1>
      <h2 style={{ ...typeStyle('headingSm'), color: semanticColor.textPrimary }}>Order history</h2>
      <a data-testid="link-orders-help" href={customerHref('/order-help')}>
        Get help with an order
      </a>
      {orders.length === 0 ? (
        <EmptyState
          title="No orders yet"
          description="Your completed checkout orders will appear here."
          action={<a href={customerHref('/categories')}>Shop Beauty Near You</a>}
        />
      ) : (
        <ul
          aria-label="Order history"
          style={{ display: 'grid', gap: space[3], listStyle: 'none', padding: 0 }}
        >
          {orders.map((order) => (
            <li key={order.id}>
              <Card>
                <h3
                  style={{
                    ...typeStyle('headingSm'),
                    color: semanticColor.textPrimary,
                    marginTop: 0,
                  }}
                >
                  <a href={customerHref(`/orders/${order.id}`)}>Order {order.id}</a>
                </h3>
                <p>Status: {order.status}</p>
                <p>Total: {money(order.total_minor, order.currency)}</p>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export function OrderDetailScreen({
  order,
  tracking = [],
}: {
  order: OrderDetail;
  tracking?: readonly OrderTracking[];
}) {
  const trackingBySubOrder = new Map(tracking.map((item) => [item.subOrderId, item]));
  return (
    <section aria-labelledby="order-detail-title">
      <h1
        id="order-detail-title"
        style={{ ...typeStyle('headingLg'), color: semanticColor.textPrimary }}
      >
        Order {order.id}
      </h1>
      <p>Status: {order.status}</p>
      <p data-testid="order-detail-total">Total: {money(order.total_minor, order.currency)}</p>
      <section aria-labelledby="sub-orders-title">
        <h2
          id="sub-orders-title"
          style={{ ...typeStyle('headingSm'), color: semanticColor.textPrimary }}
        >
          Sellers
        </h2>
        <ul aria-label="Order sellers">
          {order.subOrders.map((subOrder) => {
            const deliveryTracking = trackingBySubOrder.get(subOrder.id);
            const proofOfDelivery = deliveryTracking?.proofOfDelivery ?? null;
            const deliveredAt =
              proofOfDelivery === null ? null : deliveryDate(proofOfDelivery.deliveredAt);
            const sellerName = seller(subOrder.ownership_type, subOrder.seller_display_name);
            return (
              <li key={subOrder.id}>
                <p>Sold by {sellerName}</p>
                <p>Fulfillment: {fulfillmentMethod(subOrder.fulfillment_mode)}</p>
                <p>Status: {subOrder.status}</p>
                {deliveryTracking === undefined ? null : (
                  <section aria-label={`Tracking for ${sellerName}`}>
                    <p data-testid={`tracking-status-${subOrder.id}`}>
                      Delivery status: {statusLabel(deliveryTracking.status)}
                    </p>
                    {deliveryTracking.events.length === 0 ? (
                      <p>No delivery updates yet.</p>
                    ) : (
                      <ul aria-label={`Delivery updates for ${sellerName}`}>
                        {deliveryTracking.events.map((event, index) => (
                          <li key={`${String(event.occurredAt)}:${event.status}:${index}`}>
                            <strong>{statusLabel(event.status)}</strong>
                            {event.note === null ? null : ` — ${event.note}`}
                          </li>
                        ))}
                      </ul>
                    )}
                    {proofOfDelivery === null ? null : (
                      <section aria-label={`Proof of delivery for ${sellerName}`}>
                        <h3
                          style={{
                            ...typeStyle('headingSm'),
                            color: semanticColor.textPrimary,
                          }}
                        >
                          Proof of delivery
                        </h3>
                        <p>
                          Delivered:{' '}
                          <time dateTime={deliveredAt?.toISOString()}>
                            {deliveredAt?.toLocaleString()}
                          </time>
                        </p>
                        {proofOfDelivery.photoUrl === null ? null : (
                          <p>
                            <a href={proofOfDelivery.photoUrl} target="_blank" rel="noreferrer">
                              View delivery photo
                            </a>
                          </p>
                        )}
                        {proofOfDelivery.latitude === null ||
                        proofOfDelivery.longitude === null ? null : (
                          <p data-testid={`delivery-location-${subOrder.id}`}>
                            Location: {proofOfDelivery.latitude.toFixed(4)},{' '}
                            {proofOfDelivery.longitude.toFixed(4)}
                          </p>
                        )}
                      </section>
                    )}
                  </section>
                )}
              </li>
            );
          })}
        </ul>
      </section>
      <section aria-labelledby="order-lines-title">
        <h2
          id="order-lines-title"
          style={{ ...typeStyle('headingSm'), color: semanticColor.textPrimary }}
        >
          Items
        </h2>
        <ul aria-label="Order items">
          {order.lines.map((line) => (
            <li key={String(line.id)}>
              <p>Sold by {seller(String(line.ownership_type), String(line.seller_display_name))}</p>
              <p>Quantity: {String(line.quantity)}</p>
            </li>
          ))}
        </ul>
      </section>
      <section aria-labelledby="order-problem-title">
        <h2
          id="order-problem-title"
          style={{ ...typeStyle('headingSm'), color: semanticColor.textPrimary }}
        >
          Need help?
        </h2>
        <p>{ORDER_PROBLEM_SUPPORT_COPY}</p>
        {needsProblemSupport(order) ? (
          <a
            data-testid="link-report-order-problem"
            href={`mailto:${supportEmailAddress()}?subject=${encodeURIComponent(`Order ${order.id} problem`)}`}
          >
            Report a problem with this order
          </a>
        ) : (
          <a data-testid="link-order-help" href={customerHref('/order-help')}>
            Get order help
          </a>
        )}
      </section>
    </section>
  );
}

/**
 * Support destination for order problems. The founder owns the real mailbox, so
 * it is configuration rather than a literal: the wrong hard-coded domain would
 * silently send a customer's problem report nowhere. Beauty Burst's registered
 * domain is beautybursttech.com, so that is the default rather than a guess at
 * another domain.
 */
/**
 * Founder-approved Launch 1.0 support wording. Beauty Burst does not offer
 * standard customer returns or refunds at Launch 1.0, so this sentence must not
 * promise a remedy, a refund, or a return. It is a single exported constant so
 * no screen can drift into promising an outcome we do not offer.
 */
export const ORDER_PROBLEM_SUPPORT_COPY =
  'If something is wrong with your order, contact Beauty Burst Support so we can review the issue and determine the appropriate resolution.';

export function supportEmailAddress(): string {
  const configured = import.meta.env.VITE_CUSTOMER_SUPPORT_EMAIL;
  return configured !== undefined && configured.trim().length > 0
    ? configured.trim()
    : 'support@beautybursttech.com';
}
